import { Component, ElementRef, Host, Optional, ViewChild } from '@angular/core';
import { IReportingDetail } from 'src/app/interfaces/ireporting-detail';
import { IReportingSummary } from 'src/app/interfaces/ireporting-summary';
import { LearnerListModalComponent } from './learner-list-modal/learner-list-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { ReportingService } from 'src/app/services/reporting/reporting.service';
import { IModuleAssignmentDetail } from 'src/app/interfaces/imodule-assignment-detail';
import { GoogleAnalyticsService } from 'src/app/google-analytics.service';
import { NavigationEnd, Router } from '@angular/router';
import { ComponentPortal } from '@angular/cdk/portal';
import { CustomerAccountListModalComponent } from 'src/app/components/modals/customer-account-list-modal/customer-account-list-modal.component';
import { CdkScrollable, Overlay, OverlayPositionBuilder, ScrollStrategyOptions } from '@angular/cdk/overlay';
import { groupAccountsByCustomerAndRoles } from 'src/app/utils/accounts.util';
import { IAccountInfo } from 'src/app/interfaces/iaccount-info';
import { Subscription } from 'rxjs';
import { naturalSortObjectsArrayByKey } from 'src/app/utils/json.util';
import { ICustomerInfo } from 'src/app/interfaces/icustomer-info';
import { UserService } from 'src/app/services/general/user.service';
import { AmplitudeService } from 'src/app/services/external-tools/amplitude.service';
import { TranslationService } from 'src/app/services/translation.service';
import { TRANSLOCO_SCOPE, TranslocoService } from '@jsverse/transloco';
import { LocaleService } from 'src/app/services/locale.service';

@Component({
  selector: 'app-reporting',
  standalone: false,
    templateUrl: './reporting.component.html',
    styleUrls: ['./reporting.component.scss'],
    providers: [
    {
      provide: TRANSLOCO_SCOPE,
      useValue: 'reporting',
    },
  ],
})
export class ReportingComponent {
    isReportLoading: boolean = true;
    isReportSummaryLoading: boolean = true;
    isPracticeAssignmentLoading: boolean = true;
    liveData: IReportingDetail | undefined;
    oldData: IReportingDetail | undefined;
    alertTitle: string = '';
    alertMessage: string = '';
    showAlertBox: boolean = false;
    alertAssignment: IModuleAssignmentDetail | undefined;

    isAccountSwitcherVisible: boolean = false;
    selectedAccountInfo: IAccountInfo | undefined;
    groupedAccountsByCustomer: ICustomerInfo[] = [];

    customerAccountListDropdownVisible: boolean = false;
    componentPortal: ComponentPortal<CustomerAccountListModalComponent> | null = null;
    overlayRef: any; // Declare overlayRef property
    @ViewChild('dropdownButton') dropdownButton!: ElementRef; // Reference to the button
    accountSwitchSubscriptions: Subscription[] = [];
    reportingSummary: IReportingSummary | undefined;
    practiceLiveData: IReportingDetail | undefined;
    practiceOldData: IReportingDetail | undefined;

    constructor(
        private dialog: MatDialog,
        private userService: UserService,
        private reportingService: ReportingService,
        private gaService: GoogleAnalyticsService,
        private router: Router,
        private overlay: Overlay,
        private overlayPositionBuilder: OverlayPositionBuilder,
        private scrollStrategyOptions: ScrollStrategyOptions,
        private amplitudeService: AmplitudeService,
        private translationService: TranslationService,
        private translocoService: TranslocoService,
        private localeService: LocaleService,
        @Optional() @Host() private scrollContainer: CdkScrollable
    ) {
    }

    ngOnInit() {
        this.translationService.loadScopedTranslation('reporting');
        localStorage.setItem('returnUrl', '/reporting');
        this.amplitudeService.trackEvent('rollupreport_pageview');

        this.router.events.subscribe(event => {
            if (event instanceof NavigationEnd) {
                this.gaService.trackPageView(event.urlAfterRedirects);
            }
        });

        let accountInfos: IAccountInfo[] = localStorage.getItem('accounts') ? JSON.parse(localStorage.getItem('accounts')!) : [];
        const currentAccountUuid = localStorage.getItem('current_account_uuid');

        if (!accountInfos.length) {
            this.userService.getAuthMetadataForAllAccounts().subscribe({
                next: (res: IAccountInfo[]) => {
                    accountInfos = res;
                    localStorage.setItem('accounts', JSON.stringify(accountInfos));

                    if (localStorage.getItem('selected_account_info')) {
                        this.selectedAccountInfo = JSON.parse(localStorage.getItem('selected_account_info')!);
                    }
                    else {
                        this.selectedAccountInfo = accountInfos.find(account => account.uuid === currentAccountUuid);
                    }
                },
                error: (error) => {
                    console.error('Error fetching auth metadata for all accounts in Reporting', error);
                }
            });
        }

        // Enable AccountSwitcher if more than one account has EnablementAdmin role
        this.isAccountSwitcherVisible = accountInfos.some((account, index) =>
            account.roles.includes('EnablementAdmin') && // If currentAccount has EnablementAdmin role...
            accountInfos.slice(index + 1).some(acc => acc.roles.includes('EnablementAdmin')) // We slice accountInfos from next index to the end and check if any of them has EnablementAdmin role
        );

        if (localStorage.getItem('selected_account_info')) {
            this.selectedAccountInfo = JSON.parse(localStorage.getItem('selected_account_info')!);

            // Check is selectedAccountInfo from localStorage is in the accountInfos array
            // If not, set the selectedAccountInfo to the currentAccountUuid Info
            if (!accountInfos.some(account => account.uuid === this.selectedAccountInfo?.uuid)) {
                this.selectedAccountInfo = accountInfos.find(account => account.uuid === currentAccountUuid);
                localStorage.setItem('selected_account_info', JSON.stringify(this.selectedAccountInfo));
            }
        }
        else {
            this.selectedAccountInfo = accountInfos.find(account => account.uuid === currentAccountUuid);
        }

        const accountUuid: string = this.selectedAccountInfo?.uuid || currentAccountUuid || '';
        this.getModuleAssignmentSummary(accountUuid);
        this.getModuleAssignmentReport(accountUuid);
        this.getModuleAssignmentReport(accountUuid, true, true, 'PRACTICE');


    }
    ngOnDestroy() {
        this.accountSwitchSubscriptions.forEach(sub => sub.unsubscribe()); // Unsubscribe all
        this.customerAccountListDropdownVisible = false;

        if (this.overlayRef) {
            this.overlayRef.detach(); // Detach the overlayRef
            this.overlayRef.dispose(); // Dispose the overlay
        }
    }

    toggleDropdown() {
        if (this.customerAccountListDropdownVisible) {
            this.accountSwitchSubscriptions.forEach(sub => sub.unsubscribe()); // Unsubscribe all subscriptions
            this.overlayRef.detach(); // Detach the overlayRef
            this.overlayRef.dispose(); // Dispose the overlay
            this.customerAccountListDropdownVisible = false;
        } else {
            this.componentPortal = new ComponentPortal(CustomerAccountListModalComponent);
            // Create position strategy
            const positionStrategy = this.overlayPositionBuilder
                .flexibleConnectedTo(this.dropdownButton)
                .setOrigin(this.dropdownButton)
                .withScrollableContainers([this.scrollContainer])
                .withPositions([
                    {
                        originX: 'start',
                        originY: 'bottom',
                        overlayX: 'start',
                        overlayY: 'top',
                        offsetY: -46
                    }
                ])
            const scrollStrategy = this.scrollStrategyOptions.reposition();

            this.overlayRef = this.overlay.create({ positionStrategy, scrollStrategy }); // Create an OverlayRef with position strategy
            const componentRef = this.overlayRef.attach(this.componentPortal);

            // Pass data via Input:
            if (!this.groupedAccountsByCustomer.length) { // Sorting Customers and Accounts
                let accountInfos: IAccountInfo[] = localStorage.getItem('accounts') ? JSON.parse(localStorage.getItem('accounts')!) : [];
                accountInfos = naturalSortObjectsArrayByKey(accountInfos, 'name');
                this.groupedAccountsByCustomer = groupAccountsByCustomerAndRoles(accountInfos, ["EnablementAdmin"]);
                this.groupedAccountsByCustomer = naturalSortObjectsArrayByKey(this.groupedAccountsByCustomer, 'customerName');
            }
            componentRef.instance.accountsByCustomer = this.groupedAccountsByCustomer;
            componentRef.instance.selectedAccountInfo = this.selectedAccountInfo;
            this.customerAccountListDropdownVisible = true;

            // Subscribe to outputs:
            const clickedOutsideSubscription = componentRef.instance.clickOutside.subscribe(() => {
                this.accountSwitchSubscriptions.forEach(sub => sub.unsubscribe()); // Unsubscribe all subscriptions
                this.overlayRef.detach(); // Detach the overlayRef
                this.customerAccountListDropdownVisible = false;
            });
            // Add subscription to the array
            this.accountSwitchSubscriptions.push(clickedOutsideSubscription);

            const optionSelectedSubscription = componentRef.instance.optionSelected.subscribe((accountInfo: IAccountInfo) => {
                // Sending the Event of Clicking on an account from the account selector to Amplitude
                this.amplitudeService.trackEvent('rollupreport_click_accountselector', {
                    'selected_account_name ': accountInfo.name,
                });

                this.accountSwitchSubscriptions.forEach(sub => sub.unsubscribe()); // Unsubscribe all subscriptions
                this.overlayRef.detach(); // Detach the overlayRef
                this.customerAccountListDropdownVisible = false;
                localStorage.setItem('selected_account_info', JSON.stringify(accountInfo));
                this.selectedAccountInfo = accountInfo;
                this.isReportLoading = true;
                this.isReportSummaryLoading = true;
                this.isPracticeAssignmentLoading = true;
                this.oldData = undefined;
                this.reportingSummary = undefined;
                this.liveData = undefined;
                this.practiceOldData = undefined;
                this.practiceLiveData = undefined;
                this.getModuleAssignmentSummary(accountInfo.uuid);
                this.getModuleAssignmentReport(accountInfo.uuid);
                this.getModuleAssignmentReport(accountInfo.uuid, true, true, 'PRACTICE');
            });
            // Add subscription to the array
            this.accountSwitchSubscriptions.push(optionSelectedSubscription);
        }
    }

    getModuleAssignmentReport(accountUuid: string, fetchLiveReport: boolean = true, fetchSummary: boolean = true, moduleAssignmentType: string = 'CERTIFICATION') {
        this.reportingService.getModuleAssignmentReport(accountUuid, fetchLiveReport, fetchSummary, moduleAssignmentType).subscribe({
            next: (res: IReportingDetail) => {
                if (fetchLiveReport) {
                    if (moduleAssignmentType === 'CERTIFICATION') {
                        this.isReportLoading = false;

                        this.liveData = res;
                        this.calculateDaysLeft(this.liveData);
                        this.formatDate(this.liveData);
                        this.getAlert();
                    } else if (moduleAssignmentType === 'PRACTICE') {
                        this.isPracticeAssignmentLoading = false;
                        this.practiceLiveData = res;
                        this.calculateDaysLeft(this.practiceLiveData);
                        this.formatDate(this.practiceLiveData);
                    }
                }
                else {
                    if (moduleAssignmentType === 'CERTIFICATION') {
                        this.isReportLoading = false;
                        this.oldData = res;
                        this.calculateDaysLeft(this.oldData);
                        this.formatDate(this.oldData);
                    }
                    else if (moduleAssignmentType === 'PRACTICE') {
                        this.isPracticeAssignmentLoading = false;
                        this.practiceOldData = res;
                        this.calculateDaysLeft(this.practiceOldData);
                        this.formatDate(this.practiceOldData);
                    }
                }

            },
            error: (error) => {
                console.error('Error fetching module assignment report', error);
            }
        });
    }

    getModuleAssignmentSummary(accountUuid: string) {
        this.reportingService.getModuleAssignmentSummary(accountUuid).subscribe({
            next: (res: IReportingSummary) => {
                this.isReportSummaryLoading = false;
                console.log('Module Assignment Summary', res);
                this.reportingSummary = res;
            },
            error: (error) => {
                console.error('Error fetching module assignment report summary', error);
            }
        });
    }


    calculateDaysLeft(data: IReportingDetail) {
        data?.moduleAssignmentsDetail.forEach((assignment: any) => {
            if (assignment.dueDate) {
                // Parse the dueDate string into a Date object
                const dueDate = new Date(assignment.dueDate);
                const today = new Date();
                today.setHours(0, 0, 0, 0);

                const diffTime = dueDate.getTime() - today.getTime();
                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                assignment['daysLeft'] = diffDays;
            } else {
                assignment['daysLeft'] = null;
            }
        });
        console.log('Data with daysLeft', data);
    }

    formatDate(data: IReportingDetail) {
        data?.moduleAssignmentsDetail.forEach((assignment: any) => {
            if (assignment.dueDate) {
                const formattedDate = this.localeService.formatDate(assignment.dueDate);
                assignment.dueDate = formattedDate || this.translocoService.translate('NA', {}, 'reporting');
            } else {
                assignment.dueDate = this.translocoService.translate('NA', {}, 'reporting');
            }
        });
        console.log('Data with formatted dates', data);
    }

    openModal(): void {
        const moduleAssignmentDetail = this.alertAssignment;
        // Filter categories with showAlert true and include count and percentage
        const categoriesWithAlert = [
            { ...moduleAssignmentDetail?.notStarted, name: 'NOT_STARTED' },
            { ...moduleAssignmentDetail?.inProgress, name: 'IN_PROGRESS' },
            { ...moduleAssignmentDetail?.completed, name: 'COMPLETED' }
        ].filter(category => category.showAlert);

        const selectedAccountInfo = localStorage.getItem('selected_account_info');
        const currentAccountUuid = localStorage.getItem('current_account_uuid');
        const accountUuid = selectedAccountInfo ? JSON.parse(selectedAccountInfo).uuid : currentAccountUuid;

        this.dialog.open(LearnerListModalComponent, {
            hasBackdrop: true,
            data: {
                accountUuid: accountUuid,
                moduleAssignmentUuid: this.alertAssignment?.moduleAssignmentUuid,
                category: categoriesWithAlert[0].name,
                moduleAssignmentType: 'CERTIFICATION',
                count: categoriesWithAlert[0].count,
                percentage: categoriesWithAlert[0].percentage,
                moduleAssignmentName: moduleAssignmentDetail?.moduleAssignmentName
            }
        });
    }

    handleLoadCertificationAssignments() {
        if (!this.oldData) {
            this.getModuleAssignmentReport(this.selectedAccountInfo!.uuid, false, false);
        }
    }

    handleLoadPracticeAssignments() {
        if (!this.practiceOldData) {
            this.getModuleAssignmentReport(this.selectedAccountInfo!.uuid, false, false, 'PRACTICE');
        }
    }

    getAlert() {
        const assignments = this.liveData?.moduleAssignmentsDetail;
        const withinWeek = assignments?.filter((item) => item.daysLeft !== undefined && item.daysLeft <= 7 && item.daysLeft > 0);
        if (withinWeek?.length === 0) {
            this.showAlertBox = false;
            return;
        }

        if (withinWeek?.length === 1) {
            const assignment = withinWeek[0];
            this.alertAssignment = assignment;
            if (assignment.notStarted.count > 0) {
                assignment.notStarted.showAlert = true;
                this.showAlertBox = true;
                this.alertTitle = `${assignment.notStarted.count} ${assignment.notStarted.count > 1 ? `${this.translocoService.translate('USERS_HAVE', {}, 'reporting')}` : `${this.translocoService.translate('USER_HAS', {}, 'reporting')}`} ${this.translocoService.translate('NOT_STARTED_ASSIGNMENT_DUE', {}, 'reporting')}!`;
                this.alertMessage = `${this.translocoService.translate('THE', {}, 'reporting')} ${assignment.moduleAssignmentName} ${this.translocoService.translate('WILL_CLOSE', {}, 'reporting')} ${assignment.daysLeft} ${assignment.daysLeft && assignment.daysLeft > 1 ? `${this.translocoService.translate('DAYS', {}, 'reporting')}` : `${this.translocoService.translate('DAY', {}, 'reporting')}`}!`;
                return;
            } else if (assignment.inProgress.count > 0) {
                this.alertAssignment = assignment;
                assignment.inProgress.showAlert = true;
                this.showAlertBox = true;
                this.alertTitle = `${assignment.inProgress.count} ${assignment.inProgress.count > 1 ? `${this.translocoService.translate('USERS_HAVE', {}, 'reporting')}` : `${this.translocoService.translate('USER_HAS', {}, 'reporting')}`} ${this.translocoService.translate('NOT_COMPLETED_ASSIGNMENT_DUE', {}, 'reporting')}!`;
                this.alertMessage = `${this.translocoService.translate('THE', {}, 'reporting')} ${assignment.moduleAssignmentName} ${this.translocoService.translate('WILL_CLOSE', {}, 'reporting')} ${assignment.daysLeft} ${assignment.daysLeft && assignment.daysLeft > 1 ? `${this.translocoService.translate('DAYS', {}, 'reporting')}` : `${this.translocoService.translate('DAY', {}, 'reporting')}`}!`;
                return;
            } else {
                this.showAlertBox = false;
                return;
            }
        }
        this.soonestAssignment(withinWeek!);
    }

    soonestAssignment(withinWeek: IModuleAssignmentDetail[]) {
        // Filter out assignments where both notStarted.count and inProgress.count are greater than 0
        const filteredAssignments = withinWeek?.filter(assignment =>
            (assignment.notStarted.count > 0 || assignment.inProgress.count > 0)
        );
        const soonestAssignments = filteredAssignments?.sort((a, b) => {
            if (a.daysLeft! < b.daysLeft!) return -1;
            if (a.daysLeft! > b.daysLeft!) return 1;

            if (a.notStarted.count > b.notStarted.count) return -1;
            if (a.notStarted.count < b.notStarted.count) return 1;

            if (a.inProgress.count > b.inProgress.count) return -1;
            if (a.inProgress.count < b.inProgress.count) return 1;

            return a.moduleAssignmentName.localeCompare(b.moduleAssignmentName);
        });

        const safeAssignments = soonestAssignments || [];
        const topAssignment = safeAssignments[0];
        if (topAssignment && topAssignment.notStarted.count > 0) {
            topAssignment.notStarted.showAlert = true;
            this.alertAssignment = topAssignment;
            this.showAlertBox = true;
            this.alertTitle = `${topAssignment.notStarted.count} ${topAssignment.notStarted.count > 1 ? `${this.translocoService.translate('USERS_HAVE', {}, 'reporting')}` : `${this.translocoService.translate('USER_HAS', {}, 'reporting')}`} ${this.translocoService.translate('NOT_STARTED_ASSIGNMENT_DUE', {}, 'reporting')}!`;
            this.alertMessage = `${this.translocoService.translate('THE', {},  'reporting')} ${topAssignment.moduleAssignmentName} ${this.translocoService.translate('WILL_CLOSE', {},  'reporting')} ${topAssignment?.daysLeft} ${topAssignment?.daysLeft && topAssignment?.daysLeft > 1 ? `${this.translocoService.translate('DAYS', {},  'reporting')}` : `${this.translocoService.translate('DAY', {},  'reporting')}`}!`;
            return;
        } else if (topAssignment && topAssignment.inProgress.count > 0) {
            topAssignment.inProgress.showAlert = true;
            this.alertAssignment = topAssignment;
            this.showAlertBox = true;
            this.alertMessage = `${this.translocoService.translate('THE', {},  'reporting')} ${topAssignment.moduleAssignmentName} ${this.translocoService.translate('WILL_CLOSE', {},  'reporting')} ${topAssignment.daysLeft} ${topAssignment?.daysLeft && topAssignment?.daysLeft > 1 ? `${this.translocoService.translate('DAYS', {}, 'reporting')}` : `${this.translocoService.translate('DAY', {}, 'reporting')}`}!`;
            this.alertTitle = `${topAssignment.inProgress.count} ${topAssignment.inProgress.count > 1 ? `${this.translocoService.translate('USERS_HAVE', {},  'reporting')}` : `${this.translocoService.translate('USER_HAS', {},  'reporting')}`} ${this.translocoService.translate('NOT_COMPLETED_ASSIGNMENT_DUE', {},  'reporting')}!`;
            return;
        } else {
            this.showAlertBox = false;
            return;
        }
    }

}
